import { servicePost } from "./client.ts";
import type { InitCache } from "../types/session.ts";

interface CommonItem { TypeName?: string; sName?: string; Title?: string; sDisplayText?: string; }

/** True if a (column, displayText) pair is already in the local CommonData cache. */
export function hasCommonValue(
  cache: Pick<InitCache, "CommonData"> | null | undefined,
  column: string,
  value: string,
): boolean {
  const v = value.trim().toLowerCase();
  const common = (cache?.CommonData?.Common ?? []) as CommonItem[];
  return common.some((c) =>
    String(c.TypeName ?? c.sName ?? "").toLowerCase() === column.toLowerCase() &&
    String(c.Title ?? c.sDisplayText ?? "").trim().toLowerCase() === v);
}

/**
 * Ensure a CB value exists server-side AND in the local CommonData cache — no
 * full refresh. No-op when already present locally. On a miss: persist via the
 * (idempotent) WebSaveValuesCB and splice the returned row — which carries the
 * server-assigned `sFilterValue` — into the cache via `appendCommonValue`.
 * Errors are swallowed (the value still persists; cache catches up on next sync).
 */
export async function ensureCommonValue(
  cache: Pick<InitCache, "CommonData"> | null | undefined,
  appendCommonValue: (item: unknown) => void,
  column: CommonCacheColumn,
  value: string | null | undefined,
): Promise<void> {
  const v = (value ?? "").trim();
  if (!v || hasCommonValue(cache, column, v)) return;
  try {
    appendCommonValue(await saveCommonValue(column, v));
  } catch {
    /* non-fatal */
  }
}

/** Server returns the row written to S_Admin_CB_Values. Field names mirror
 *  the existing CommonCache shape so callers can splice into the cache if
 *  they want, though current call sites prefer a full `refreshCache()`. */
export interface CommonCacheValue {
  sName: string;
  sDisplayText: string;
  sFilterValue: string;
  CalcTS: number;
}

/** TypeName values accepted by `WebSaveValuesCB`. Restricted to the columns
 *  WebAdminCC currently surfaces via `<ComboBoxInput>`. Server tolerates
 *  others, but listing them here keeps mistakes out at compile time. */
export type CommonCacheColumn =
  | "sKioskType"
  | "sChain"
  | "sCountry"
  | "sDistrict"
  | "sState";

/** Register a new dropdown value in the `S_Admin_CB_Values` table. Mirrors
 *  WebAdmin2's `SaveValuesCB`. For columns where the server auto-increments
 *  a numeric ID (sChain / sCountry / sDistrict) the `id` arg is ignored;
 *  for sKioskType the server stores whatever string is passed (we default
 *  to `value` to match WebAdmin2's IsBoth=false flow).
 *
 *  Throws on server failure (matches the project's other API wrappers) so
 *  callers can surface the message via toast and decide whether to splice
 *  into the local cache. */
export async function saveCommonValue(
  column: CommonCacheColumn,
  value: string,
  id?: string,
): Promise<CommonCacheValue> {
  const result = await servicePost<CommonCacheValue>("WebSaveValuesCB", {
    column,
    value,
    id: id ?? value,
  });
  if (result.bError || !result.oData) {
    throw new Error(result.sMessage || `Failed adding ${column} value "${value}"`);
  }
  return result.oData;
}
